import Loader from '@justgiving/loading/es/Loading';
import React, { FunctionComponent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FitnessApplicationActivityType,
  ManualFitnessEntrySettingsFragment,
} from '../../../types/__generated__';
import { useModalToggle } from '../../hooks/useModalToggle';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import SettingsModal from '../SettingsModal/SettingsModal';
import fragment from './ManualFitnessEntryFragment.gql';
import ManualFitnessEntryModal from './ManualFitnessEntryModal/ManualFitnessEntryModal';

const FRAGMENT = buildQuery(fragment);

interface ManualFitnessEntryProps {
  opener: (props: { onOpen: () => void }) => ReactNode;
  onSubmit?: () => void;
}

const ManualFitnessEntry: FunctionComponent<ManualFitnessEntryProps> = ({
  opener,
  onSubmit: onSubmitCallback,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const {
    isOpen: isModalOpen,
    setOpen: setModalOpen,
    ref,
    Portal,
  } = useModalToggle();
  const [isLoading, setIsLoading] = useState(false);

  const result = usePageQuery<ManualFitnessEntrySettingsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (
    !UsePageQueryResult.hasData(result) ||
    !result?.data?.application?.settings?.measurementSystem ||
    !result?.data?.application?.settings?.subscribedActivities
  ) {
    return null;
  }

  const {
    legacyId,
    type,
    slug,
    title,
    application: {
      settings: { measurementSystem, subscribedActivities },
    },
  } = result.data;

  const openModal = async () => {
    setIsLoading(true);
    setModalOpen(true);
    await result.refetch();
    setIsLoading(false);
  };
  const closeModal = () => setModalOpen(false);
  const onSubmit = () => {
    // eslint-disable-next-line no-unused-expressions
    onSubmitCallback?.();
    closeModal();
  };

  return (
    <>
      {opener({ onOpen: openModal })}
      {isModalOpen && (
        <Portal
          background={true}
          focusLock={true}
          scrollLock={true}
          scrollIsolation={true}
        >
          <SettingsModal
            title={title}
            onCloseRequest={closeModal}
            aria-label={t('manualFitness.modalLabel')}
          >
            {isLoading ? (
              <Loader size="medium" colour="purple" />
            ) : (
              <ManualFitnessEntryModal
                pageId={legacyId}
                pageType={type}
                pageSlug={slug}
                measurementSystem={measurementSystem}
                subscribedActivities={
                  subscribedActivities.filter(
                    x => x,
                  ) as FitnessApplicationActivityType[]
                }
                onSubmit={onSubmit}
                onCancel={closeModal}
                ref={ref}
              />
            )}
          </SettingsModal>
        </Portal>
      )}
    </>
  );
};

export default ManualFitnessEntry;
